/**
 * Resolve the home path a signed-in user lands on for a given role.
 *
 * BLUF-1323 — driven by the live role catalogue rather than a hardcoded
 * mapping. Each role's landing surface comes from the `[landing: /path]`
 * marker in its Cognito group description. Grant a role a new home by
 * editing the group description, not this file.
 *
 * Returns `/` as a safe fallback when the role is null, missing from
 * the catalogue, or has no landing marker. Refusing to guess is
 * deliberate: silently redirecting to another role's home is worse
 * than surfacing "no home configured".
 *
 * IMPORTANT for callers on the LandingPage: if you invoke this from a
 * component mounted at `/` and it returns `/`, you'll cause a
 * re-render loop (Navigate to `/` → LandingPage renders → Navigate
 * again). Ensure every role that can be authenticated has a
 * `[landing: ...]` marker on its Cognito group description — that's a
 * data requirement, not a code one. Both advisor groups
 * (InternalBrokers, ExternalBrokers) carry `[landing:
 * /portal/advisor-dashboard]` so signed-in advisors don't loop.
 *
 * Used by:
 *   - LandingPage: redirect already-signed-in users away from '/' to
 *     their role's home.
 *   - AccessDeniedPage: "Return to Home" button.
 *   - SelectRolePage: navigate after a successful role pick.
 *   - RoleSwitcher: hard-reload target on mid-session role switch.
 *   - ProducerSwitcher: after a successful producer switch.
 */

import type { CanonicalRole, RoleDefinition } from './constants.js';

export function homePathFor(
  role: CanonicalRole | null,
  catalogue: RoleDefinition[] | null,
): string {
  if (!role || !catalogue) return '/';
  const def = catalogue.find((r) => r.name === role);
  return def?.landingPath ?? '/';
}
